import React, {useEffect, useState} from 'react';
import {classes} from './CartItemOptions.st.css';
import {Text, TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUp';
import {CartItemDataHook} from '../../CartItem';
import classNames from 'classnames';
import s from '../../CartItem.scss';
import {useControllerProps} from '../../../../../../domain/controllers/ControllerContext';

export enum CartItemOptionsDataHook {
  collapseExpandButton = 'CartItemOptionsDataHook.collapseExpandButton',
}

export interface CartItemOptionsProps {
  itemId: number | string;
  options: {title: string; value: string}[];
  maxOptionValLength: number;
  collapseSettings: {
    numberOfOptionsToCollapseAfter: number;
    showMoreTitle: string;
    showLessTitle: string;
  };
  className?: string;
}

export const CartItemOptions = ({
  itemId,
  options,
  maxOptionValLength,
  collapseSettings,
  className,
}: CartItemOptionsProps) => {
  const {numberOfOptionsToCollapseAfter, showMoreTitle, showLessTitle} = collapseSettings;
  const {shouldUpdateCartItemMoreOptionsFont} = useControllerProps().cartStore;

  const [shouldCollapseAndExpand] = useState(
    Boolean(numberOfOptionsToCollapseAfter && options.length > numberOfOptionsToCollapseAfter)
  );
  const [numberOfOptionsToShow, setNumberOfOptionsToShow] = useState(
    shouldCollapseAndExpand ? numberOfOptionsToCollapseAfter : options.length
  );
  const [isCollapsed, setIsCollapsed] = useState(shouldCollapseAndExpand);

  useEffect(() => {
    setNumberOfOptionsToShow(isCollapsed ? numberOfOptionsToCollapseAfter : options.length);
  }, [isCollapsed, numberOfOptionsToCollapseAfter, options]);

  const getValue = (value: string) => {
    if (value?.length <= maxOptionValLength) {
      return value;
    }
    return `${value.slice(0, maxOptionValLength - 3)}...`;
  };

  const renderOption = (option: {title: string; value: string}) => {
    const {title, value} = option;
    const trimmedOptionTitle = title?.trim();

    if (!trimmedOptionTitle) {
      return (
        <li key={`option-item-${itemId}-value-${value}`}>
          <Text className={classes.option}>{getValue(value)}</Text>
        </li>
      );
    }
    return (
      <li key={`option-item-${itemId}-value-${title}`}>
        <Text className={classes.option}>
          {title}: {getValue(value)}
        </Text>
      </li>
    );
  };

  return (
    <div className={classNames(classes.root, className)}>
      <ul
        className={
          shouldUpdateCartItemMoreOptionsFont ? classes.options : /* istanbul ignore next */ classes.optionsOld
        }
        data-hook={CartItemDataHook.Options}>
        {options.slice(0, numberOfOptionsToShow).map(renderOption)}
      </ul>
      {shouldCollapseAndExpand && (
        <TextButton
          className={
            shouldUpdateCartItemMoreOptionsFont
              ? classNames(classes.collapseButton, s.collapseButton)
              : /* istanbul ignore next */ classNames(classes.collapseButtonOld, s.collapseButton)
          }
          suffixIcon={isCollapsed ? <ChevronDown /> : <ChevronUp />}
          priority={TextButtonPriority.secondary}
          data-hook={CartItemOptionsDataHook.collapseExpandButton}
          onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? showMoreTitle : showLessTitle}
        </TextButton>
      )}
    </div>
  );
};
